import MaterialTable from '@material-table/core';
import {
	ControlPointDuplicateOutlined,
	DeleteOutlineOutlined,
	DoneOutline,
	EditOutlined,
} from '@mui/icons-material';
import { Box, Card, CardContent } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { InUseDialog } from '../../components/inUseDialog/inUseDialog';
import { PageAppBar } from '../../components/pageAppBar';
import { deletePaymentProfile } from '../../graphql/mutations';
import { listPaymentProfiles } from '../../graphql/queries';
import { cardStyle } from '../../theme';
import { checkPaymentProfileUsage } from '../../tools/checkUsage';
import { fetchAllPages } from '../../tools/graphqlPaging';

export function C2AProfiles() {
	const client = generateClient();
	// const classes = useStyles();
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [profiles, setProfiles] = useState([]);
	const [toDelete, setToDelete] = useState(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [inUseOpen, setInUseOpen] = useState(false);
	const [inUseInfo, setInUseInfo] = useState(null);

	useEffect(() => {
		async function getData() {
			setLoading(true);
			// Paged so all C2A profiles are listed, not just page one.
			const allProfiles = await fetchAllPages({
				client,
				query: listPaymentProfiles,
				resultKey: 'listPaymentProfiles',
				variables: { tenant: userContext.tenantId },
			});
			setProfiles(
				allProfiles.sort(
					(a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
				),
			);
			setLoading(false);
		}
		if (userContext.tenantId) {
			getData();
		}
	}, [userContext.tenantId]);

	async function handleDelete() {
		setLoading(true);
		// Check if profile is in use by a communication profile
		const isInUse = await checkPaymentProfileUsage(
			toDelete,
			userContext.tenantId,
		);
		if (isInUse.inUse) {
			setInUseInfo({ message: isInUse.message, usedBy: isInUse.usedBy || [] });
			setInUseOpen(true);
			setConfirmDeleteOpen(false);
			setToDelete(null);
			setLoading(false);
			return;
		}

		const deleteResult = await client.graphql({
			query: deletePaymentProfile,
			variables: { input: { id: toDelete } },
		});
		console.log(deleteResult);
		//if profile is successful in deleting remove it from the current list.
		if (deleteResult.data) {
			const deletedProfile = deleteResult.data.deletePaymentProfile;
			const curProfiles = [...profiles];
			curProfiles.splice(
				curProfiles.findIndex((e) => e.id === deletedProfile.id),
				1,
			);
			setProfiles([...curProfiles]);
		}

		setToDelete(null);
		setConfirmDeleteOpen(false);
		setLoading(false);
	}

	function handleCancelDelete() {
		setToDelete(null);
		setConfirmDeleteOpen(false);
	}

	function handleCloseInUse() {
		setInUseOpen(false);
		setInUseInfo(null);
	}

	return (
		<Box>
			<PageAppBar
				title="Digital Profiles"
				description="Set up Omni Channel Messaging and C2A Actions"
				actionOneText={
					userContext.securityOptions.digitalChannels > 1 ? '+ Profile' : ''
				}
				actionOneHandler={() => {
					navigate('/digital-profiles/new');
				}}
				actionOneDisabled={userContext.securityOptions.digitalChannels < 2}
			/>
			<Card variant="outlined" sx={cardStyle}>
				<CardContent>
					<MaterialTable
						components={{
							Container: (props) => <Box {...props} elevation={0} />,
						}}
						title=""
						data={profiles}
						columns={[
							{ title: 'Name', field: 'name' },
							{
								title: 'SMS',
								render: (rowData) => {
									console.log(rowData);
									if (JSON.parse(rowData.channels).smsEnabled) {
										return <DoneOutline color="primary" />;
									}
								},
							},
							{
								title: 'Email',
								render: (rowData) => {
									console.log(rowData);
									if (JSON.parse(rowData.channels).emailEnabled) {
										return <DoneOutline color="primary" />;
									}
								},
							},
						]}
						isLoading={loading}
						options={{
							actionsColumnIndex: -1,
							pageSize: 10,
							searchFieldStyle: {
								marginBottom: '16px',
								marginleft: '-28px',
							},
							headerStyle: {
								borderTop: '1px solid #e0e0e0',
								padding: '16px',
							},
							searchFieldVariant: 'outlined',
							paginationType: 'stepped',
						}}
						actions={[
							{
								disabled: userContext.securityOptions.digitalChannels < 2,
								icon: () => <EditOutlined color="primary" />,
								tooltip: 'Edit Profile',
								onClick: (event, rowData) => {
									navigate(`/digital-profiles/${rowData.id}`);
								},
							},
							{
								disabled: userContext.securityOptions.communicationProfiles < 2,
								icon: () => <ControlPointDuplicateOutlined color="primary" />,
								tooltip: 'Clone Profile',
								onClick: (event, rowData) => {
									navigate(`/digital-profiles/${rowData.id}?op=clone`);
								},
							},
							(rowData) =>
								(function () {
									const disabled =
										userContext.securityOptions.digitalChannels < 2;
									return {
										icon: () => (
											<DeleteOutlineOutlined
												color={disabled ? 'disabled' : 'primary'}
											/>
										),
										tooltip: 'Delete Profile',
										onClick: (event, rowData) => {
											setToDelete(rowData.id);
											setConfirmDeleteOpen(true);
										},
										disabled,
									};
								})(),
						]}
						onRowClick={(event, rowData) => {
							navigate(`/digital-profiles/${rowData.id}`);
						}}
					/>
				</CardContent>
			</Card>
			<ConfirmDialog
				open={confirmDeleteOpen}
				title="Delete Profile"
				description="Are you sure you want to delete this profile?"
				actionOneText="Delete"
				actionOneHandler={() => {
					handleDelete();
				}}
				actionTwoText="Cancel"
				actionTwoHandler={() => {
					handleCancelDelete();
				}}
			/>
			<InUseDialog
				open={inUseOpen}
				title="Digital Profile In Use"
				message={inUseInfo?.message}
				usedBy={inUseInfo?.usedBy || []}
				onClose={handleCloseInUse}
			/>
		</Box>
	);
}
